/* Copyright (c) 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { setContext } from "apollo-link-context";
import { ApolloProvider } from "react-apollo";
import { ApolloProvider as ApolloHooksProvider } from "react-apollo-hooks";
import { createHttpLink } from "apollo-link-http";
import { useAuth0 } from "./commonComponents/auth";
import { ApolloClient } from "apollo-client";
import { InMemoryCache } from "apollo-cache-inmemory";
import { onError } from "apollo-link-error";
import { Observable } from "apollo-link";
import { GraphQLError } from "graphql";

interface CustomGraphQLError extends GraphQLError {
  errorType: string;
}

type Props = {
  children: React.ReactNode;
};

export function AuthorizedApolloProvider({ children }: Props) {
  const { getIdTokenClaims, getAccessTokenSilently } = useAuth0();

  const customFetch = (uri: RequestInfo, options: any) => {
    const { operationName } = JSON.parse(options.body);
    const fetchUrl = window.Cypress ? `${uri}?opname=${operationName}` : uri;
    return fetch(fetchUrl, options);
  };

  const httpLink = createHttpLink({
    uri: process.env.REACT_APP_GRAPHQL_ENDPOINT,
    fetch: customFetch,
  });

  const authLink = setContext(async (_, { headers }) => {
    // get the authentication token from local storage / memory if it exists
    const token = await getIdTokenClaims();

    return {
      headers: {
        ...headers,
        Authorization: token?.__raw,
      },
    };
  });

  const onErrorLink = onError(({ graphQLErrors, operation, forward }) => {
    if (graphQLErrors) {
      for (let err of graphQLErrors) {
        switch ((err as CustomGraphQLError).errorType) {
          case "UnauthorizedException":
            // errorType is set to UnauthorizedException
            // when AuthenticationError thrown in resolver

            // modify the operation context with a new token
            // retry the request, returning the new observable
            return new Observable((observer) => {
              getAccessTokenSilently()
                .then(async () => {
                  const token = await getIdTokenClaims();
                  const oldHeaders = operation.getContext().headers;
                  operation.setContext({
                    headers: {
                      ...oldHeaders,
                      Authorization: token.__raw,
                    },
                  });
                })
                .then(() => {
                  const subscriber = {
                    next: observer.next.bind(observer),
                    error: observer.error.bind(observer),
                    complete: observer.complete.bind(observer),
                  };

                  // Retry last failed request
                  forward(operation).subscribe(subscriber);
                })
                .catch((error) => {
                  // No refresh or client token available, we force user to login
                  observer.error(error);
                });
            });
        }
      }
    }
  });

  const link = onErrorLink.concat(httpLink);

  const client = new ApolloClient({
    link: authLink.concat(link),
    cache: new InMemoryCache(),
    defaultOptions: {
      query: {
        fetchPolicy: "no-cache",
      },
      watchQuery: {
        fetchPolicy: "no-cache",
      },
    },
  });

  return (
    <ApolloProvider client={client}>
      <ApolloHooksProvider client={client}>{children}</ApolloHooksProvider>
    </ApolloProvider>
  );
}
